// DB Input > "PDO 이미지 (자동 분류)" — 폴더째(하위 폴더 포함) 골라 한 번에 올린다.
//   · 파일명으로 PDO Image(BF) / PDO Pathology(H&E·IHC) 를 서버가 판정한다 (server/guest_web/util/pictureUpload.js)
//   · 이미 있는 이름은 건너뛰고, 오가노이드 정보가 없어도 올리되 알려준다
//   · 결과 목록을 텍스트로 보여주고 .txt 로 내려받는다
//   흐름: 파일 선택 → /api/checkPictureUpload (미리보기) → /api/uploadPicturesAuto 를 여러 번 나눠 호출

import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import styles from './PictureAutoUpload.module.css';
import { pictureCaption, parsePictureCaption } from '../common/pictureCaption';

const BATCH_FILES = 8;
const BATCH_BYTES = 24 * 1024 * 1024;

// 상태 → 묶음 순서 · 라벨 · 색
const STATUS = {
    READY: { order: 1, label: '업로드 예정', tone: 'ready' },
    UPLOADED: { order: 0, label: '업로드 완료', tone: 'done' },
    RENAMED: { order: 0, label: '번호 붙여 업로드', tone: 'done' },
    // 이름만 겹친 것 — 업로드 때 서버가 내용을 비교한다 (같으면 건너뜀, 다르면 번호를 붙여 올림)
    NAME_TAKEN: { order: 2, label: '이름 겹침 · 업로드 때 비교', tone: 'warn' },
    DUP_IN_BATCH: { order: 3, label: '선택 안에서 이름 겹침 · 업로드 때 비교', tone: 'warn' },
    EXISTS: { order: 2, label: '같은 사진이 이미 있음', tone: 'skip' },
    UNKNOWN_TYPE: { order: 4, label: '사진 종류 판별 불가', tone: 'warn' },
    BAD_NAME: { order: 5, label: '오가노이드 코드 형식 아님', tone: 'warn' },
    TOO_LONG: { order: 6, label: '이름이 45자 초과', tone: 'warn' },
    FAILED: { order: 7, label: '실패', tone: 'error' },
    NOT_IMAGE: { order: 8, label: '이미지 아님', tone: 'muted' },
};
const KIND_LABEL = { LM: 'PDO Image', PATH: 'PDO Pathology' };
const UPLOADABLE = new Set(['READY', 'NAME_TAKEN', 'DUP_IN_BATCH']);
const DONE = new Set(['UPLOADED', 'RENAMED']);

// 화면 캡션 — 오가노이드 정보 사진 아래·편집 창과 같은 공용 규칙 (BF_P12_40X (2))
const caption = (name) => pictureCaption(name);

// 끌어다 놓은 폴더를 하위까지 펼친다
async function filesFromDrop(items) {
    const out = [];
    const walk = (entry, prefix) => new Promise((resolve) => {
        if (entry.isFile) {
            entry.file((f) => { out.push({ file: f, path: prefix + f.name }); resolve(); }, () => resolve());
        } else if (entry.isDirectory) {
            const reader = entry.createReader();
            const all = [];
            const readBatch = () => reader.readEntries(async (ents) => {
                if (!ents.length) {
                    for (const e of all) await walk(e, prefix + entry.name + '/');
                    resolve();
                } else { all.push(...ents); readBatch(); }
            }, () => resolve());
            readBatch();
        } else resolve();
    });
    const entries = [...items].map((it) => it.webkitGetAsEntry && it.webkitGetAsEntry()).filter(Boolean);
    for (const e of entries) await walk(e, '');
    return out;
}

function nowText() {
    const d = new Date();
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function PictureAutoUpload({ onBusyChange }) {
    const { t } = useTranslation();
    const [items, setItems] = useState([]);       // { file, path, url }
    const [rows, setRows] = useState([]);         // 서버 판정 결과 (index = items 순서)
    const [checking, setChecking] = useState(false);
    const [progress, setProgress] = useState(null); // { done, total }
    const [uploadedOnce, setUploadedOnce] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const folderRef = useRef(null);
    const fileRef = useRef(null);

    const busy = checking || !!progress;
    useEffect(() => { onBusyChange && onBusyChange(busy); }, [busy, onBusyChange]);
    // 썸네일 URL 은 닫을 때만 해제한다 (추가 선택 때는 기존 항목을 그대로 쓴다)
    const itemsRef = useRef(items);
    itemsRef.current = items;
    useEffect(() => () => itemsRef.current.forEach((it) => it.url && URL.revokeObjectURL(it.url)), []);

    const addFiles = async (list) => {
        if (!list.length) return;
        const merged = [...items];
        const known = new Set(merged.map((it) => it.path));
        list.forEach(({ file, path }) => {
            if (known.has(path)) return;
            known.add(path);
            const previewable = /\.(jpe?g|png|bmp)$/i.test(file.name);
            merged.push({ file, path, url: previewable ? URL.createObjectURL(file) : null });
        });
        setItems(merged);
        setUploadedOnce(false);
        setChecking(true);
        try {
            const res = await axios.post('/api/checkPictureUpload', {
                files: merged.map((it) => ({ path: it.path, name: it.file.name })),
            });
            setRows(res.data.rows || []);
        } catch (err) {
            alert(t('파일 확인 중 오류가 발생했습니다.'));
        } finally {
            setChecking(false);
        }
    };

    const onPick = (e) => {
        const list = [...e.target.files].map((f) => ({ file: f, path: f.webkitRelativePath || f.name }));
        e.target.value = '';
        addFiles(list);
    };

    const onDrop = async (e) => {
        e.preventDefault();
        setDragOver(false);
        if (busy) return;
        addFiles(await filesFromDrop(e.dataTransfer.items));
    };

    const reset = () => {
        items.forEach((it) => it.url && URL.revokeObjectURL(it.url));
        setItems([]); setRows([]); setUploadedOnce(false);
    };

    const counts = useMemo(() => {
        const c = { total: rows.length, LM: 0, PATH: 0, noOrg: 0, pending: 0, clash: 0 };
        rows.forEach((r) => {
            c[r.status] = (c[r.status] || 0) + 1;
            if (UPLOADABLE.has(r.status) || DONE.has(r.status)) {
                c[r.kind] += 1;
                if (r.noOrganoid) c.noOrg += 1;
            }
            if (UPLOADABLE.has(r.status)) c.pending += 1;
            if (r.status === 'NAME_TAKEN' || r.status === 'DUP_IN_BATCH') c.clash += 1;
        });
        c.skipped = (c.UNKNOWN_TYPE || 0) + (c.BAD_NAME || 0) + (c.TOO_LONG || 0);
        return c;
    }, [rows]);

    const upload = async () => {
        const targets = rows.filter((r) => UPLOADABLE.has(r.status));
        if (!targets.length) return;
        const batches = [];
        let cur = [], bytes = 0;
        targets.forEach((r) => {
            const size = items[r.index].file.size;
            if (cur.length && (cur.length >= BATCH_FILES || bytes + size > BATCH_BYTES)) { batches.push(cur); cur = []; bytes = 0; }
            cur.push(r); bytes += size;
        });
        if (cur.length) batches.push(cur);

        setProgress({ done: 0, total: targets.length });
        const next = rows.map((r) => ({ ...r }));
        let done = 0;
        for (const batch of batches) {
            const form = new FormData();
            batch.forEach((r) => form.append('images', items[r.index].file));
            form.append('meta', JSON.stringify(batch.map((r) => ({ index: r.index, path: r.path, name: items[r.index].file.name }))));
            try {
                const res = await axios.post('/api/uploadPicturesAuto', form, { headers: { 'Content-Type': 'multipart/form-data' } });
                (res.data.results || []).forEach((x) => {
                    if (x.index != null && next[x.index]) next[x.index] = { ...next[x.index], ...x };
                });
            } catch (err) {
                const msg = err?.response?.status === 403 ? t('권한이 없습니다') : (err?.message || 'error');
                batch.forEach((r) => { next[r.index] = { ...next[r.index], status: 'FAILED', message: msg }; });
            }
            done += batch.length;
            setRows(next.map((r) => ({ ...r })));
            setProgress({ done, total: targets.length });
        }
        setProgress(null);
        setUploadedOnce(true);
    };

    const sorted = useMemo(() => rows.slice().sort((a, b) =>
        (STATUS[a.status]?.order ?? 9) - (STATUS[b.status]?.order ?? 9)
        || (a.noOrganoid === b.noOrganoid ? 0 : a.noOrganoid ? -1 : 1)
        || String(a.name).localeCompare(String(b.name))), [rows]);

    // 결과 텍스트 — 화면에 보여주고 그대로 .txt 로 저장한다
    const report = useMemo(() => {
        if (!rows.length) return '';
        const L = [];
        L.push(`${t('PDO 이미지 업로드')} ${uploadedOnce ? t('결과') : t('미리보기')} — ${nowText()}`);
        L.push(`${t('전체')} ${counts.total} · PDO Image ${counts.LM} · PDO Pathology ${counts.PATH}`
            + (uploadedOnce ? ` · ${t('번호 붙여 업로드')} ${counts.RENAMED || 0} · ${t('같은 사진이 이미 있음')} ${counts.EXISTS || 0}` : ` · ${t('이름 겹침')} ${counts.clash}`)
            + ` · ${t('건너뜀')} ${counts.skipped}`
            + ` · ${t('실패')} ${counts.FAILED || 0} · ${t('제외')} ${counts.NOT_IMAGE || 0}`);
        L.push('');
        const section = (title, list, withKind = true, note = '') => {
            if (!list.length) return;
            L.push(`[${title}] ${list.length}`);
            if (note) L.push(`  ※ ${note}`);
            list.forEach((r) => L.push(`  ${withKind ? (KIND_LABEL[r.kind] || '-').padEnd(15) : ''}${r.pictureKey || r.name}    (${r.path})${r.message ? '  ' + r.message : ''}`));
            L.push('');
        };
        const byName = (a, b) => String(a.pictureKey || a.name).localeCompare(String(b.pictureKey || b.name));
        const by = (s) => rows.filter((r) => r.status === s).sort(byName);
        const doneOrReady = uploadedOnce ? 'UPLOADED' : 'READY';
        section(uploadedOnce ? t('업로드 완료') : t('업로드 예정'), by(doneOrReady).filter((r) => !r.noOrganoid));
        if (uploadedOnce) {
            const renamed = by('RENAMED');
            if (renamed.length) {
                L.push(`[${t('이름이 겹치지만 다른 사진 — 번호를 붙여 업로드')}] ${renamed.length}`);
                renamed.forEach((r) => L.push(`  ${(KIND_LABEL[r.kind] || '-').padEnd(15)}${r.renamedFrom}  →  ${r.pictureKey}    (${r.path})`));
                L.push('');
            }
        } else {
            section(t('이름 겹침 — 업로드 때 내용 비교 (같은 사진이면 건너뛰고, 다르면 번호를 붙여 올림)'),
                [...by('NAME_TAKEN'), ...by('DUP_IN_BATCH')]);
        }
        section(t('오가노이드 정보 없음 — 업로드는 됨'), [...by(doneOrReady), ...by('RENAMED')].filter((r) => r.noOrganoid), true,
            t('같은 코드로 오가노이드 정보가 등록되면 오가노이드 화면에 자동으로 나타납니다'));
        section(t('같은 사진이 이미 있음 — 건너뜀'), by('EXISTS'));
        const noMag = rows.filter((r) => (UPLOADABLE.has(r.status) || DONE.has(r.status)) && !parsePictureCaption(r.pictureKey || r.name).mag).sort(byName);
        section(t('배율 표기를 찾지 못함 — 업로드는 됨 (화면 캡션에 배율이 빠짐)'), noMag);
        section(t('사진 종류 판별 불가 — 건너뜀 (파일명에 BF · H&E 표기 필요)'), by('UNKNOWN_TYPE'), false);
        section(t('오가노이드 코드 형식 아님 — 건너뜀'), by('BAD_NAME'), false);
        section(t('이름이 45자 초과 — 건너뜀'), by('TOO_LONG'));
        section(t('실패'), by('FAILED'));
        section(t('이미지 아님 — 제외'), by('NOT_IMAGE'), false);
        return L.join('\n');
    }, [rows, counts, uploadedOnce, t]);

    const downloadReport = () => {
        const blob = new Blob(['﻿' + report], { type: 'text/plain;charset=utf-8' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `PDO_image_upload_${nowText().replace(/[-: ]/g, '')}.txt`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    };

    const copyReport = () => { navigator.clipboard && navigator.clipboard.writeText(report); };

    const readyCount = counts.pending;

    return (
        <div className={styles.wrap}>
            <div
                className={`${styles.dropZone} ${dragOver ? styles.dropOver : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
            >
                <div className={styles.dropTitle}>{t('폴더를 끌어다 놓거나 선택하세요')}</div>
                <div className={styles.dropHint}>
                    {t('하위 폴더까지 이미지를 모두 찾습니다. 파일명으로 PDO Image(BF) · PDO Pathology(H&E) 를 자동으로 나눕니다.')}
                </div>
                <div className={styles.pickButtons}>
                    <button className={styles.btnPrimary} disabled={busy} onClick={() => folderRef.current.click()}>{t('폴더 선택')}</button>
                    <button className={styles.btn} disabled={busy} onClick={() => fileRef.current.click()}>{t('파일 선택')}</button>
                    {items.length > 0 && <button className={styles.btnText} disabled={busy} onClick={reset}>{t('초기화')}</button>}
                </div>
                <input ref={folderRef} type="file" webkitdirectory="" directory="" multiple hidden onChange={onPick} />
                <input ref={fileRef} type="file" accept="image/*,.tif,.tiff" multiple hidden onChange={onPick} />
            </div>

            {checking && <div className={styles.note}>{t('파일을 확인하는 중입니다…')}</div>}

            {rows.length > 0 && (
                <>
                    <div className={styles.summary}>
                        <span className={styles.chip}>{t('전체')} <b>{counts.total}</b></span>
                        <span className={`${styles.chip} ${styles.tone_ready}`}>PDO Image <b>{counts.LM}</b></span>
                        <span className={`${styles.chip} ${styles.tone_ready}`}>PDO Pathology <b>{counts.PATH}</b></span>
                        {counts.noOrg > 0 && <span className={`${styles.chip} ${styles.tone_warn}`}>{t('오가노이드 정보 없음')} <b>{counts.noOrg}</b></span>}
                        {counts.clash > 0 && <span className={`${styles.chip} ${styles.tone_warn}`}>{t('이름 겹침')} <b>{counts.clash}</b></span>}
                        {(counts.RENAMED || 0) > 0 && <span className={`${styles.chip} ${styles.tone_done}`}>{t('번호 붙여 업로드')} <b>{counts.RENAMED}</b></span>}
                        {(counts.EXISTS || 0) > 0 && <span className={`${styles.chip} ${styles.tone_skip}`}>{t('같은 사진이 이미 있음')} <b>{counts.EXISTS}</b></span>}
                        {counts.skipped > 0 && <span className={`${styles.chip} ${styles.tone_warn}`}>{t('건너뜀')} <b>{counts.skipped}</b></span>}
                        {(counts.FAILED || 0) > 0 && <span className={`${styles.chip} ${styles.tone_error}`}>{t('실패')} <b>{counts.FAILED}</b></span>}
                        {(counts.NOT_IMAGE || 0) > 0 && <span className={`${styles.chip} ${styles.tone_muted}`}>{t('제외')} <b>{counts.NOT_IMAGE}</b></span>}
                    </div>

                    <div className={styles.tableBox}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th style={{ width: 58 }}></th>
                                    <th>{t('사진 이름')}</th>
                                    <th style={{ width: 120 }}>{t('구분')}</th>
                                    <th style={{ width: 130 }}>{t('오가노이드')}</th>
                                    <th style={{ width: 170 }}>{t('상태')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sorted.map((r) => {
                                    const it = items[r.index] || {};
                                    const st = STATUS[r.status] || STATUS.FAILED;
                                    return (
                                        <tr key={r.index} className={st.tone === 'muted' ? styles.rowMuted : ''}>
                                            <td>
                                                <div className={styles.thumb}>
                                                    {it.url ? <img src={it.url} alt="" loading="lazy" />
                                                        : <span>{(String(r.name).split('.').pop() || '').toUpperCase().slice(0, 4)}</span>}
                                                </div>
                                            </td>
                                            <td>
                                                <div className={styles.name}>{r.pictureKey || r.name}</div>
                                                <div className={styles.sub}>
                                                    {caption(r.pictureKey || r.name) && <span className={styles.caption}>{caption(r.pictureKey || r.name)}</span>}
                                                    <span className={styles.path} title={r.path}>{r.path}</span>
                                                </div>
                                                {r.renamedFrom && <div className={styles.orgWarn}>{t('원래 이름')}: {r.renamedFrom}</div>}
                                                {r.kind && !parsePictureCaption(r.pictureKey || r.name).mag && <div className={styles.orgWarn}>{t('배율 표기 없음')}</div>}
                                            </td>
                                            <td>{r.kind ? <span className={`${styles.kind} ${r.kind === 'LM' ? styles.kindLM : styles.kindPATH}`}>{KIND_LABEL[r.kind]}</span> : <span className={styles.dash}>—</span>}</td>
                                            <td className={styles.org}>
                                                {r.organoid || <span className={styles.dash}>—</span>}
                                                {r.noOrganoid && <div className={styles.orgWarn}>{t('오가노이드 정보 없음')}</div>}
                                            </td>
                                            <td>
                                                <span className={`${styles.badge} ${styles['tone_' + st.tone]}`}>{t(st.label)}</span>
                                                {r.message && <div className={styles.sub}>{r.message}</div>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className={styles.actions}>
                        {progress ? (
                            <div className={styles.progress}>
                                <div className={styles.progressBar}><div style={{ width: `${(progress.done / progress.total) * 100}%` }} /></div>
                                <span>{t('업로드 중')} {progress.done} / {progress.total}</span>
                            </div>
                        ) : (
                            <span className={styles.note}>
                                {readyCount > 0
                                    ? t('이름이 겹치면 내용을 비교해 같은 사진은 건너뛰고 다른 사진은 번호를 붙여 올립니다. 오가노이드 정보가 없는 사진도 올라갑니다.')
                                    : uploadedOnce ? t('업로드를 마쳤습니다. 결과 목록을 확인하세요.') : t('올릴 수 있는 사진이 없습니다.')}
                            </span>
                        )}
                        <button className={styles.btnPrimary} disabled={busy || readyCount === 0} onClick={upload}>
                            {t('업로드')} ({readyCount})
                        </button>
                    </div>

                    <div className={styles.reportHead}>
                        <span>{uploadedOnce ? t('결과 목록') : t('미리보기 목록')}</span>
                        <div>
                            <button className={styles.btnText} onClick={copyReport}>{t('복사')}</button>
                            <button className={styles.btn} onClick={downloadReport}>{t('목록 다운로드 (.txt)')}</button>
                        </div>
                    </div>
                    <pre className={styles.report}>{report}</pre>
                </>
            )}
        </div>
    );
}

export default PictureAutoUpload;
